/**
 * Shared Indian Standard Time (IST) Date/Time Formatting Utilities.
 * Strictly uses explicit IANA timezone "Asia/Kolkata".
 * Does not rely on client/browser default timezone or manual 5h30m arithmetic.
 */

import type { TimeRangeKey } from "../types/telemetry.ts";

export const IST_TIMEZONE = "Asia/Kolkata";

const standardISTFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: IST_TIMEZONE,
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const bucketTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: IST_TIMEZONE,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const bucketDateFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: IST_TIMEZONE,
  day: "2-digit",
  month: "short",
});

const bucketTooltipFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: IST_TIMEZONE,
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export interface FormatDateTimeOptions {
  preserveSubseconds?: boolean;
}

/**
 * Formats a date/timestamp in Indian Standard Time (IST).
 * Example: 2026-10-06T20:00:00Z -> "07 Oct 2026, 01:30:00 IST"
 *
 * Missing or invalid timestamps display an honest placeholder "—".
 */
export function formatDateTimeIST(
  input: string | number | Date | null | undefined,
  options: FormatDateTimeOptions = {}
): string {
  if (input === null || input === undefined || input === "") {
    return "—";
  }

  let date: Date;
  let subseconds = "";

  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) {
      return "—";
    }
    date = new Date(trimmed);
    if (options.preserveSubseconds) {
      const match = trimmed.match(/\.(\d+)/);
      if (match) {
        subseconds = `.${match[1]}`;
      }
    }
  } else if (typeof input === "number") {
    if (isNaN(input) || !isFinite(input)) {
      return "—";
    }
    date = new Date(input);
    if (options.preserveSubseconds) {
      const ms = date.getMilliseconds();
      if (ms > 0) {
        subseconds = `.${String(ms).padStart(3, "0")}`;
      }
    }
  } else if (input instanceof Date) {
    date = input;
    if (options.preserveSubseconds) {
      const ms = date.getMilliseconds();
      if (ms > 0) {
        subseconds = `.${String(ms).padStart(3, "0")}`;
      }
    }
  } else {
    return "—";
  }

  if (isNaN(date.getTime())) {
    return "—";
  }

  const base = standardISTFormatter.format(date);
  if (subseconds) {
    return `${base}${subseconds} IST`;
  }
  return `${base} IST`;
}

/**
 * Formats a chart bucket label in IST given its epoch millisecond start time.
 * For "all" time range: "07 Oct"
 * For bounded time ranges ("15m", "60m", "24h"): "01:30"
 */
export function formatBucketLabelIST(
  startTimeMs: number,
  timeRange: TimeRangeKey = "60m"
): string {
  if (startTimeMs === null || startTimeMs === undefined || isNaN(startTimeMs) || !isFinite(startTimeMs)) {
    return "—";
  }
  const date = new Date(startTimeMs);
  if (isNaN(date.getTime())) {
    return "—";
  }

  if (timeRange === "all") {
    return bucketDateFormatter.format(date);
  }
  return bucketTimeFormatter.format(date);
}

/**
 * Formats a chart bucket tooltip in IST.
 * Example: 07 Oct 2026, 01:30 IST
 */
export function formatBucketTooltipIST(
  startTimeMs: number | null | undefined
): string {
  if (startTimeMs === null || startTimeMs === undefined || isNaN(startTimeMs) || !isFinite(startTimeMs)) {
    return "—";
  }
  const date = new Date(startTimeMs);
  if (isNaN(date.getTime())) {
    return "—";
  }

  return `${bucketTooltipFormatter.format(date)} IST`;
}
